// turn-timer-update.ts — handler for the 'turn_timer_update' frame.
//
// PHP publisher: options/game/turns/push_turn_timer_inc.php broadcasts the GM's bare
// turn-timer {remaining, running} integers to the encounter channel on discrete GM control
// actions (resume / pause / reset / +seconds / next turn) — NOT per tick. Player clients run
// their own local 1s countdown between events (window.ftApplyTurnTimerMirror installs a ticker),
// so this is a handful of messages per turn, no DB touch, no polling.
//
// Two early exits:
//   • isGM viewer — the GM browser IS the authority (runTurnTimerTick drives its own UI and
//     fired this broadcast); consuming its own echo would fight the local tick. Skip.
//   • stale-encounter — same defense-in-depth as roll-insert.ts: a player mid-rotation can
//     briefly hold a subscription to a previous encounter; drop frames whose encounter_id
//     doesn't match the encounter the player is currently viewing (active encounter cache context).

import type { WSMessage } from './types';

type WSTurnTimerUpdateMessage = Extract<WSMessage, { type: 'turn_timer_update' }>;

function handleTurnTimerUpdate(msg: WSTurnTimerUpdateMessage): void {
    const encounterId = msg?.encounter_id || '';
    if (!encounterId) {
        ftDebugLog('ws', { warn: 'turn_timer_update: no encounter_id', msg });
        return;
    }

    // GM is the source of truth — never let the echo overwrite the GM's own live tick.
    if (window.FateTide?.user?.isGM) {
        return;
    }

    // Stale-encounter gate (mirror of roll-insert.ts). Only drop when we KNOW the player's
    // current encounter and it differs.
    const curEncounterId = String(window.ftGetActiveEncounterId?.() || '').trim();
    if (curEncounterId && encounterId !== curEncounterId) {
        ftDebugLog('ws', { phase: 'turn_timer_update-stale-encounter-drop', msgEnc: encounterId, curEnc: curEncounterId });
        return;
    }

    const remaining = parseInt(String(msg?.remaining ?? '0'), 10);
    const running = msg?.running ? 1 : 0;

    // Delegate to the template.js mirror state/ticker/badge updater (the badge lives in the
    // sidelog encounter summary, rendered by the encounter-summary bundle).
    const apply = window.ftApplyTurnTimerMirror;
    if (typeof apply === 'function') {
        apply(Number.isNaN(remaining) ? 0 : remaining, running, encounterId);
    }

    ftDebugLog('ws', { phase: 'turn_timer_update-applied', encounterId, remaining, running, reason: msg?.reason });
}

window.handleTurnTimerUpdate = handleTurnTimerUpdate;

export { handleTurnTimerUpdate };
